import {PlayerColor} from '@colonist/game-states'
import {RoomClientState} from '../../../../../shared/scripts/RoomClientState.ts'
import {ClientUserState} from '../../../../../shared/scripts/ClientUserState'
import {GameColor} from '../../../../../shared/scripts/GameColor'
import {UIGameCanvasController} from '../../../../../shared/scripts/ui_game/UIGameCanvasController'
import {UIGameManager} from '../../../../../shared/scripts/ui_game/UIGameManager'
import {UIGameFeed} from '../../../../../shared/scripts/ui_game/ui_game_feed/UIGameFeed'
import {createDivIn, createHeaderIn, createImgIn, createParagraphIn, createSelectIn, setElementInnerHTML, setElementOnClick} from '../../../../../shared/scripts/ui_utils/DomUtils'
import {LobbyImage} from '@colonist/types'
import {RoomPlayOrderSelectionData, RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {socket} from '../../../../../shared/scripts/EntryShared'
import {TranslationObject} from '@colonist/i18n'

export class UIRoomPlayOrderSelectionView extends UIGameFeed {
    private readonly gameManager: UIGameManager
    private readonly playerViews: HTMLDivElement
    private readonly titleView: HTMLDivElement
    private readonly seedOrderData: RoomSeedOrderSelectionData
    private playOrderSelectionData: RoomPlayOrderSelectionData
    private myOrderSelectionBox: HTMLSelectElement
    private myColor: PlayerColor
    private roomState: RoomClientState

    constructor(gameManager: UIGameManager, roomState: RoomClientState, playOrderData: RoomPlayOrderSelectionData, seedOrderData: RoomSeedOrderSelectionData) {
        super('play-order-selection-view-container', 'game-chat-block')
        this.playOrderSelectionData = playOrderData
        this.seedOrderData = seedOrderData
        this.gameManager = gameManager
        this.roomState = roomState
        const canvas = gameManager.canvasController
        this.setInitialSizeAndPosition(canvas)
        window.addEventListener('resize', () => this.resizeSelectionView(canvas.appW, canvas.appH))

        this.titleView = createHeaderIn(this.container, {key: 'strings:roomPage.orderSelection.playOrderSelection.title'}, 'play-order-selection-title')
        this.playerViews = createDivIn(this.container, 'play-order-selection-view-container')
        this.createSessionViews()
        this.mainBlock.style.backgroundImage = 'none'
    }

    update(newRoomState: RoomClientState, data: RoomPlayOrderSelectionData) {
        this.roomState = newRoomState
        this.playOrderSelectionData = data

        this.updateTitle()
        this.createSessionViews()
    }

    private setInitialSizeAndPosition(canvas: UIGameCanvasController) {
        const canvasX = canvas.responsiveController.getCanvasX()
        const canvasY = canvas.responsiveController.getCanvasY()
        const w = canvas.chatWidth
        const h = (canvas.appH - canvas.itemSize * 5.5)
        this.setSize(w, h)
        this.setPosition(canvasX, canvasY)
    }

    private setPosition(canvasX: number, canvasY: number) {
        const MARGIN = this.gameManager.canvasController.margin
        this.setPositionRight(canvasX + (MARGIN * 2))
        this.setPositionTop(((canvasY + MARGIN)))
    }

    private setSize(w: number, h: number) {
        const margin = this.gameManager.canvasController.margin
        const divHeight = (h)

        this.scale(1)
        this.mainBlock.style.marginTop = margin + 'px'
        this.setHeight(divHeight * 2)
        this.setWidth((w) - (margin * 2))
    }

    private resizeSelectionView(newAppW: number, newAppH: number) {
        const canvas = this.gameManager.canvasController
        const wRatioChanged = newAppW / canvas.appW
        const hRatioChanged = newAppH / canvas.appH
        const canvasX = canvas.responsiveController.getCanvasX()
        const canvasY = canvas.responsiveController.getCanvasY()
        const w = wRatioChanged * canvas.chatWidth
        const h = hRatioChanged * (canvas.appH - canvas.itemSize * 5.5)
        this.setSize(w, h)
        this.setPosition(canvasX, canvasY)
    }

    private updateTitle() {
        const currentSelectorUsername = this.roomState.getSessions().find((session) => session.selectedColor == this.playOrderSelectionData.currentSelector)?.username ?? ''
        const mySession = this.roomState.getSessions().find((session) => session.userSessionId == socket.controller.session.id)
        const amICurrentSelector = this.playOrderSelectionData.currentSelector == mySession?.selectedColor
        const titleText: TranslationObject = amICurrentSelector ? {key: 'strings:roomPage.orderSelection.playOrderSelection.yourTurn'} : {key: 'strings:roomPage.orderSelection.playOrderSelection.playerSelecting', options: {username: `${currentSelectorUsername}`}}
        setElementInnerHTML(this.titleView, titleText)
    }

    private createSessionViews() {
        setElementInnerHTML(this.playerViews, {key: 'strings:colonistCommon.utils.#buffer', options: {value: ''}})
        const sessions = this.roomState.getSessions()
        const mySession = this.roomState.getSessions().find((session) => session.userSessionId == socket.controller.session.id)
        const sessionsOrderedBySeed = this.seedOrderData.seedOrder.sort((a, b) => a[0] - b[0])
        const takenPlayOrders = this.playOrderSelectionData.playOrder.map((singleSession) => singleSession[1])
        sessionsOrderedBySeed?.forEach((session, index) => {
            const playerColor = session[1]
            const sessionDiv = createDivIn(this.playerViews, 'session-view')
            sessionDiv.style.background = GameColor.getLighterGradientOfPlayerColor(playerColor)

            const icon = sessions.find((clientState) => clientState.selectedColor == playerColor)?.icon ?? LobbyImage.InfoIcon
            const userIcon = createImgIn(sessionDiv, icon, 'user-icon')
            const userName = sessions.find((clientState) => clientState.selectedColor == playerColor)?.username ?? 'NameNotFound'
            const username = createParagraphIn(sessionDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: userName}}, 'username')
            const dropdown = createSelectIn(sessionDiv, 'play-order-selection-dropdown', undefined, (value) => this.onDropdownValueChange(value, playerColor))
            UIRoomPlayOrderSelectionView.setDropdownOptions(dropdown, sessions, takenPlayOrders)

            sessionDiv.appendChild(userIcon)
            sessionDiv.appendChild(username)
            sessionDiv.appendChild(dropdown)

            const previouslySetEntryOfPlayer = this.playOrderSelectionData.playOrder.find(([color, order]) => color == playerColor)
            const previouslySelectedPlayOrder = previouslySetEntryOfPlayer?.[1]
            const isPlayOrderSetBefore = previouslySelectedPlayOrder != undefined
            if(isPlayOrderSetBefore) dropdown.value = previouslySelectedPlayOrder.toString()

            const isMySelectionBox = playerColor == mySession?.selectedColor
            const isCurrentSelector = playerColor == this.playOrderSelectionData?.currentSelector
            const shouldDisableDropdown = !isMySelectionBox || isPlayOrderSetBefore || !isCurrentSelector
            dropdown.disabled = shouldDisableDropdown
            if(isMySelectionBox) {
                this.myOrderSelectionBox = dropdown
                this.myColor = playerColor
            }
            if(shouldDisableDropdown) return
            const checkmarkButton = createDivIn(this.playerViews, 'btn_general btn_general_check checkmark_button')
            setElementOnClick(checkmarkButton, () => this.onConfirmSelection())
        })
    }

    private onConfirmSelection() {
        const order = parseInt(this.myOrderSelectionBox.value, 10)
        if(isNaN(order)) return
        this.myOrderSelectionBox.disabled = true
        socket.roomClient.setPlayOrder(order)
    }

    private onDropdownValueChange(value: string, selector: PlayerColor) {
        if(selector != this.playOrderSelectionData?.currentSelector) return
        this.myOrderSelectionBox.value = value
    }

    private static setDropdownOptions(dropdown: HTMLSelectElement, sessions: ClientUserState[], takenPlayOrders: number[] | undefined) {
        const defaultOption = document.createElement('option')
        defaultOption.value = '-'
        defaultOption.textContent = '-'
        dropdown.appendChild(defaultOption)
        for(let i = 1; i <= sessions.length; i++) {
            const option = document.createElement('option')
            option.value = i.toString()
            option.textContent = i.toString() + UIRoomPlayOrderSelectionView.getOrdinalIndicatorForPlayOrder(i)
            if(takenPlayOrders?.includes(i)) option.disabled = true
            dropdown.appendChild(option)
        }
    }

    private static getOrdinalIndicatorForPlayOrder(order: number): string {
        if(order == 1) return 'st'
        if(order == 2) return 'nd'
        if(order == 3) return 'rd'
        return 'th'
    }
}
